import { Material, Mesh, Object3D } from "three";
import { waitTill } from "../util/Wait";
import { logger } from "../util/Logger";

const cacheMap: any = {};
const log = false;

export interface Loader {
  load(usl: string, options?: {}): any;
}
export class LoaderManager {
  private _loader: Loader;

  constructor(loader: Loader) {
    this._loader = loader;
  }

  async load(url: string, options?: {}, clone = true) {
    if (!url || url === "") return undefined;

    log && logger.info("load begins for ", url);
    if (cacheMap[url] && cacheMap[url].done && cacheMap[url].asset) {
      return clone
        ? cloneAsset(cacheMap[url].asset, true)
        : cacheMap[url].asset;
    }

    if (cacheMap[url] && !cacheMap[url].done) {
      await waitTill(() => {
        const entry = cacheMap[url];
        return !entry || !!entry.done;
      });
      const queued = cacheMap[url];
      if (!queued?.asset) return undefined;
      return clone ? cloneAsset(queued.asset, true) : queued.asset;
    }

    cacheMap[url] = { done: false, asset: null };

    let asset: any;
    try {
      asset = await this._loader.load(url, options);
    } catch (error) {
      logger.warn("asset load failed", url, error);
      cacheMap[url] = undefined;
      return undefined;
    }

    if (asset) {
      cacheMap[url].asset = asset;
      cacheMap[url].done = true;
      // İlk yükleme de önbellekteki materyalleri paylaşmasın; sonraki yüklemelerle aynı klonlama.
      return cloneAsset(asset, true);
    }

    log && logger.info("load ends in error for ", url);
    cacheMap[url] = undefined;
    return undefined;
  }
}

export function clearAssetCache() {
  for (const key in cacheMap) {
    if (cacheMap[key] != null && cacheMap[key].asset instanceof Object3D) {
      cacheMap[key].asset?.traverse((child: any) => {
        child.geometry?.dispose();
        child.material?.dispose();
      });
      cacheMap[key].asset?.removeFromParent();
      cacheMap[key] = undefined;
    }
  }
}

export function cloneAsset(object: any, rec = true) {
  if (object instanceof Object3D) {
    const objectClone = object.clone(rec);
    // Her materyal bir kez klonlanır; aynı materyali paylaşan mesh'ler klonu da paylaşır.
    // Mesh materyali dizi de olabilir (çoklu materyal), dizi .clone() bilmez.
    const clones = new Map<Material, Material>();
    const cloneOnce = (material: Material) => {
      let clone = clones.get(material);
      if (!clone) {
        clone = material.clone();
        clones.set(material, clone);
      }
      return clone;
    };
    objectClone.traverse((o) => {
      if (!(o instanceof Mesh) || !o.material) return;
      o.material = Array.isArray(o.material)
        ? o.material.map(cloneOnce)
        : cloneOnce(o.material);
    });
    return objectClone;
  }
  return object.clone(rec);
}
